/**
 * `DIALOG.lineDelete` (W2-07; DESIGN.md §4.21 "Delete line"): for lines with notes or children. States how many
 * notes go with them and what happens to the child lines: keep them where they are (detach), attach them to this
 * line's parent, or delete them too (with their notes). Runs `Core.lines.deleteLines` as one undoable step.
 * Lock only blocks editing, so a choice that would change a locked line (a locked child gets another parent with
 * every choice; "Delete them too" also deletes locked descendants) is disabled with the reason, and the delete
 * button says why when nothing may go ahead.
 */

import { useState } from 'react';
import type { DialogProps } from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import { useT } from '../../i18n/useT';
import { useTr } from '../../ui/bridge';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { useEditorValue, useServicesStore } from './hooks';
import { CHILDREN_POLICIES, deleteLinesNow, deleteLockedReason, lineTitle, type ChildrenPolicy } from './lineActions';
import { m } from './lines.messages';
import { deleteImpact } from './lineModel';
import s from './Lines.module.css';

export function LineDeleteDialog(p: DialogProps<'lineDelete'>) {
  const t = useT();
  const tr = useTr();
  const store = useServicesStore(p.services);
  // Re-render when a line is locked or unlocked while the dialog is open.
  useEditorValue(p.services, (st) => st.filters.lockedLines);
  const [chosen, setPolicy] = useState<ChildrenPolicy | null>(null);
  const [error, setError] = useState<MessageRef | null>(null);
  if (store === null) return null;
  const impact = deleteImpact(store.doc, store.idx, p.props.lines);
  const count = impact.lines.length;
  const first = impact.lines[0];
  const name = count === 1 && first !== undefined ? lineTitle(store, first) : '';
  const hasChildren = impact.children.length > 0;
  const grandparent = first !== undefined && count === 1 ? (store.doc.lines.get(first)?.parent ?? null) : null;
  const refusal = (value: ChildrenPolicy): MessageRef | null => deleteLockedReason(p.services, impact, value);
  // Without children only "detach" applies (nothing to detach). The first allowed choice is the default.
  const policy: ChildrenPolicy = !hasChildren
    ? 'detach'
    : chosen !== null && refusal(chosen) === null
      ? chosen
      : (CHILDREN_POLICIES.find((value) => refusal(value) === null) ?? chosen ?? 'detach');
  const blocked = count === 0 ? null : refusal(policy);
  const shown = error ?? blocked;

  const close = (): void => {
    p.close(null);
  };
  const confirm = (): void => {
    if (count === 0) {
      p.close(null);
      return;
    }
    if (blocked !== null) {
      setError(blocked);
      return;
    }
    const reason = deleteLinesNow(p.services, impact.lines, policy);
    if (reason !== null) {
      setError(reason);
      return;
    }
    p.close({ applied: true });
  };

  const choice = (value: ChildrenPolicy, title: string, text: string) => {
    const refused = refusal(value);
    return (
      <label className={s.choice} data-refused={refused === null ? undefined : 'true'}>
        <input
          type="radio"
          name="children"
          value={value}
          checked={policy === value}
          disabled={refused !== null}
          onChange={() => {
            setPolicy(value);
            setError(null);
          }}
        />
        <span className={s.choiceTitle}>{title}</span>
        <span className={s.choiceText}>{text}</span>
        {refused !== null && (
          <span className={s.choiceRefused}>
            <Icon name="Lock" size={12} />
            <span>{tr(refused)}</span>
          </span>
        )}
      </label>
    );
  };

  return (
    <Dialog
      open={true}
      size="sm"
      title={t(m.deleteTitle, { count, name })}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t(m.cancel)}
          </Button>
          <Button variant="danger" icon="Trash2" disabledReason={blocked} onClick={confirm}>
            {t(m.deleteConfirm, {
              count: count + (hasChildren && policy === 'deleteSubtree' ? impact.subtree.length : 0),
            })}
          </Button>
        </>
      }
    >
      <div className={s.form}>
        {count === 0 ? (
          <p>{t(m.deleteNothing)}</p>
        ) : (
          <p>{impact.notes > 0 ? t(m.deleteNotes, { notes: impact.notes, count }) : t(m.deleteNoNotes, { count })}</p>
        )}
        {hasChildren && (
          <fieldset className={s.choices}>
            <legend className={s.label}>{t(m.deleteChildren, { count: impact.children.length })}</legend>
            {choice('detach', t(m.policyDetach), t(m.policyDetachHelp))}
            {choice(
              'reparentToGrandparent',
              t(m.policyReparent),
              grandparent === null
                ? t(m.policyReparentNone)
                : t(m.policyReparentHelp, { parent: lineTitle(store, grandparent) }),
            )}
            {choice(
              'deleteSubtree',
              t(m.policySubtree),
              t(m.policySubtreeHelp, { lines: impact.subtree.length, notes: impact.subtreeNotes }),
            )}
          </fieldset>
        )}
        <p className={s.help}>{t(m.deleteUndoable)}</p>
        {shown !== null && (
          <div className={s.error} role="alert">
            <Icon name={error === null ? 'Lock' : 'CircleX'} size={16} />
            <span>{tr(shown)}</span>
          </div>
        )}
      </div>
    </Dialog>
  );
}
